import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Building2, MapPin, Wrench, ArrowRight, X, Plus } from 'lucide-react';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { createWorkspace, user } = useAuth();
  const { t, texts } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [companyName, setCompanyName] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [customService, setCustomService] = useState('');
  const [customCity, setCustomCity] = useState('');

  const PRESET_SERVICES = texts.presetServices || [];
  const PRESET_CITIES = texts.presetCities || [];

  const toggleService = (service) => {
    if (selectedServices.includes(service)) {
      setSelectedServices(prev => prev.filter(s => s !== service));
    } else {
      setSelectedServices(prev => [...prev, service]);
    }
  };

  const toggleCity = (city) => {
    if (selectedCities.includes(city)) {
      setSelectedCities(prev => prev.filter(c => c !== city));
    } else {
      setSelectedCities(prev => [...prev, city]);
    }
  };

  const addCustomService = () => {
    if (customService && !selectedServices.includes(customService)) {
      setSelectedServices(prev => [...prev, customService]);
      setCustomService('');
    }
  };

  const addCustomCity = () => {
    if (customCity && !selectedCities.includes(customCity)) {
      setSelectedCities(prev => [...prev, customCity]);
      setCustomCity('');
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await createWorkspace({
        name: companyName,
        services: selectedServices,
        cities: selectedCities,
      });
      navigate('/dashboard');
    } catch (err) {
      console.error('Error creating workspace:', err);
      alert(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-background p-4 flex items-center justify-center">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight">
            Space<span className="text-primary">LeadX</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('onboarding.welcomeUser', { name: user?.name })}
          </p>
        </div>

        {/* Progress */}
        <div className="flex justify-center mb-8 gap-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-16 h-2 rounded-full transition-colors ${
                s <= step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        <Card className="shadow-xl">
          {/* Step 1: Company Name */}
          {step === 1 && (
            <>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle>{t('onboarding.companyName')}</CardTitle>
                    <CardDescription>
                      {t('onboarding.companyNameDesc')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="company-name">{t('settings.companyName')}</Label>
                  <Input
                    id="company-name"
                    type="text"
                    placeholder={t('onboarding.companyPlaceholder')}
                    className="h-12 text-lg"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    data-testid="company-name-input"
                  />
                </div>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!companyName.trim()}
                  className="w-full h-12 font-bold"
                  data-testid="step1-next-btn"
                >
                  {t('onboarding.continue')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}

          {/* Step 2: Services */}
          {step === 2 && (
            <>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Wrench className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle>{t('onboarding.yourServices')}</CardTitle>
                    <CardDescription>
                      {t('onboarding.servicesDesc')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  {PRESET_SERVICES.map((service) => (
                    <Badge
                      key={service}
                      variant={selectedServices.includes(service) ? 'default' : 'outline'}
                      className={`cursor-pointer px-4 py-2 text-sm transition-all ${
                        selectedServices.includes(service) 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-primary/10'
                      }`}
                      onClick={() => toggleService(service)}
                      data-testid={`service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {service}
                      {selectedServices.includes(service) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>

                {/* Custom service input */}
                <div className="flex gap-2">
                  <Input
                    placeholder={t('onboarding.addCustomService')}
                    value={customService}
                    onChange={(e) => setCustomService(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomService()}
                    className="h-10"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={addCustomService}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Selected services */}
                {selectedServices.length > 0 && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">
                      {t('onboarding.servicesSelected', { count: selectedServices.length })}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {selectedServices.map(s => (
                        <Badge key={s} variant="secondary" className="text-xs">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12"
                  >
                    {t('common.back')}
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={selectedServices.length === 0}
                    className="flex-1 h-12 font-bold"
                    data-testid="step2-next-btn"
                  >
                    {t('onboarding.continue')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Cities */}
          {step === 3 && (
            <>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle>{t('onboarding.serviceAreas')}</CardTitle>
                    <CardDescription>
                      {t('onboarding.areasDesc')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  {PRESET_CITIES.map((city) => (
                    <Badge
                      key={city}
                      variant={selectedCities.includes(city) ? 'default' : 'outline'}
                      className={`cursor-pointer px-4 py-2 text-sm transition-all ${
                        selectedCities.includes(city) 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-primary/10'
                      }`}
                      onClick={() => toggleCity(city)}
                      data-testid={`city-${city.toLowerCase()}`}
                    >
                      {city}
                      {selectedCities.includes(city) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>

                {/* Custom city input */}
                <div className="flex gap-2">
                  <Input
                    placeholder={t('onboarding.addCity')}
                    value={customCity}
                    onChange={(e) => setCustomCity(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomCity()}
                    className="h-10"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={addCustomCity}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Selected cities */}
                {selectedCities.length > 0 && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">
                      {t('onboarding.citiesSelected', { count: selectedCities.length })}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {selectedCities.map(c => (
                        <Badge key={c} variant="secondary" className="text-xs">
                          {c}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1 h-12"
                  >
                    {t('common.back')}
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={selectedCities.length === 0 || isLoading}
                    className="flex-1 h-12 font-bold"
                    data-testid="create-workspace-btn"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {t('onboarding.createWorkspace')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
